import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  Accordion,
  AccordionAction,
  AccordionContent,
  AccordionIcon,
  AccordionItem,
  AccordionTitle,
  Button,
  Card,
  DatePicker,
  Input,
  Popover,
  PopoverAction,
  PopoverContent,
  Spinner,
  Textarea,
  toast,
  Upload,
  UploadBody,
  UploadFooter,
  UploadText,
} from 'keep-react'

import { Wrapper } from 'components'
import { PageType } from 'api/types/page'
import { pageService } from 'api/services/page'
import { deltaToHtmlQuill } from 'utils/quillToHtml'
import { Calendar, Download, Trash } from 'phosphor-react'
import { AppContext } from 'contexts/AppContext'
import { useFormik } from 'formik'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import { emailService } from 'api/services/email'

export interface UploadedFile {
  path?: string
  name?: string
  lastModified?: number
  lastModifiedDate?: string
  type?: string
  size?: number
}

export const Page = () => {
  const { settings, setIsCallingApi } = useContext(AppContext)
  const { slug } = useParams<{ slug: string }>()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)

  //loaded page data
  const [data, setData] = useState<PageType | null>(null)
  const [content, setContent] = useState<string>('')

  //is claims process page
  const [isClaimsProcess, setIsClaimsProcess] = useState<boolean>(false)

  //uploaded files state
  const [files, setFiles] = useState<File[]>([])

  //is sending form
  const [isSending, setIsSending] = useState<boolean>(false)

  //get page details
  const getPage = async () => {
    setIsLoading(true)
    try {
      const response = (await pageService.getPageBySlug({
        slug: String(slug),
      })) as PageType
      setData(response as PageType)
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar a página.')
    }
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      policyNo: '',
      date: '',
      description: '',
      measures: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      setIsSending(true)
      setIsCallingApi(true)
      try {
        const body = {
          name: values.name,
          email: values.email,
          policyNo: values.policyNo,
          date: values.date,
          description: values.description,
          measures: values.measures,
          files: files,
        }
        let html = '<h3>Participação de sinistro</h3>'
        html += `<p>participação de sinistro recebido de ${body.email} com os seguintes dados:</p>`
        html += Object.entries(body)
          .map(([key, value]) => {
            if (key !== 'files') {
              return `<p><strong>${key}:</strong> ${value}</p>`
            } else {
              return ''
            }
          })
          .join('')

        await emailService.sendEmail({
          html,
          emailTo: settings?.emailThatReceivesSinistersParticipations || '',
          subject: `Nova participação de sinistro - ${values.policyNo}`,
          attachments: files,
        })

        toast.success(
          'A sua participação de sinistro foi submetida com sucesso. Irá ser contactado pelos nossos serviços no prazo máximo de 48 horas úteis.',
          {
            duration: 12000,
          },
        )
      } catch (error) {
        toast.error('Não foi possível submeter o sinistro.', {
          duration: 12000,
        })
      } finally {
        setIsSending(false)
        setIsCallingApi(false)
      }
    },
  })

  const onDrop = (acceptedFiles: any) => {
    if (files.length > 0) {
      setFiles([...files, ...acceptedFiles])
    } else {
      setFiles(acceptedFiles)
    }
  }

  // initial data fetch
  useEffect(() => {
    if (!slug || !settings) return
    if (settings && settings.pagesClaimsProcess === slug) {
      setIsClaimsProcess(true)
    } else {
      setIsClaimsProcess(false)
    }
    getPage()
  }, [settings, slug])

  useEffect(() => {
    if (!data?.content) return
    const html = deltaToHtmlQuill(data.content)
    setContent(html)
  }, [data?.content])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <h1 className="pb-10 text-xxxl font-medium text-primary-600">
              {data.title}
            </h1>

            <div className="grid grid-col-1 lg:grid-cols-[2fr_1fr] gap-4 w-full mb-4">
              <div>
                <Card className="w-full max-w-full p-4 md:p-6">
                  <div
                    dangerouslySetInnerHTML={{ __html: content }}
                    className="[&_h2]:text-xxl [&_h3]:text-xl [&_h4]:text-lg [&_h2]:py-4 [&_h3]:py-3 [&_h4]:py-2 [&_p]:text-body-4 [&_p]:pb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:pb-4 [&_ul]:text-body-4 [&_ol]:list-disc [&_ol]:pl-6 [&_ol]:pb-4 [&_ol]:text-body-4 [&_a]:text-primary-600 [&_a]:underline"
                  ></div>
                </Card>
              </div>

              <div className="space-y-4">
                {data.files && data.files.length > 0 && (
                  <Card className="w-full max-w-full p-4 md:p-6">
                    <h2 className="text-lg font-medium text-metal-900">
                      Ficheiros
                    </h2>

                    <div className="flex flex-col gap-4 mt-4">
                      {data.files.map((file, index) => (
                        <Button
                          key={`doc${index}`}
                          color="secondary"
                          variant="softBg"
                          className="w-full gap-2 justify-start items-center h-auto"
                          onClick={() => window.open(file.uri, '_blank')}
                        >
                          <Download size={16} className="flex-shrink-0" />
                          <span className="whitespace-normal">
                            {file.label}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </Card>
                )}

                {data.qAs && data.qAs.length > 0 && (
                  <Card className="w-full max-w-full p-4 md:p-6">
                    <h2 className="text-lg font-medium text-metal-900">
                      Perguntas Frequentes
                    </h2>

                    <Accordion flush={true} type="single" collapsible={true}>
                      {data.qAs.map((qa, index) => (
                        <AccordionItem
                          value={`item-${index}`}
                          key={`qa${index}`}
                        >
                          <AccordionAction className="px-0 gap-2">
                            <AccordionTitle className="text-left text-body-4 font-medium">
                              {qa.question}
                            </AccordionTitle>
                            <AccordionIcon />
                          </AccordionAction>
                          <AccordionContent className="text-left px-0 text-body-4">
                            {qa.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Card>
                )}
              </div>

              {isClaimsProcess && (
                <Card className="w-full max-w-full p-4 md:p-6">
                  <h2 className="text-lg font-medium text-metal-900 pb-4">
                    Formulário Online
                  </h2>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        className="block w-full"
                        placeholder="Nome completo do segurado"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                      />

                      <Input
                        id="email"
                        name="email"
                        type="email"
                        className="block w-full"
                        placeholder="Email de contacto"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                      <Input
                        id="policyNo"
                        name="policyNo"
                        type="text"
                        className="block w-full"
                        placeholder="N.º de apólice"
                        value={formik.values.policyNo}
                        onChange={formik.handleChange}
                      />

                      <Popover>
                        <PopoverAction
                          asChild
                          className="relative w-full flex items-center"
                        >
                          <Button
                            color="secondary"
                            size="lg"
                            className="w-full flex items-center justify-start gap-2.5 border border-metal-100 text-body-4"
                            variant="outline"
                          >
                            <Calendar size={20} className="text-metal-400" />
                            {formik.values.date ? (
                              format(new Date(formik.values.date), 'dd/MM/yyyy')
                            ) : (
                              <span className="text-metal-200">
                                Data da ocorrência
                              </span>
                            )}
                          </Button>
                        </PopoverAction>
                        <PopoverContent
                          align="start"
                          className="max-w-min border-0"
                        >
                          <DatePicker
                            mode="single"
                            selected={new Date(formik.values.date)}
                            onSelect={(d: any) =>
                              d && formik.setFieldValue('date', d)
                            }
                            showOutsideDays={true}
                            locale={pt}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <Textarea
                      id="description"
                      name="description"
                      className="block w-full placeholder:text-metal-200"
                      placeholder="Breve descrição do sinistro/circunstância"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      rows={6}
                    />

                    <Textarea
                      id="measures"
                      name="measures"
                      className="block w-full placeholder:text-metal-200"
                      placeholder="Medidas já adotadas"
                      value={formik.values.measures}
                      onChange={formik.handleChange}
                      rows={6}
                    />

                    <Upload options={{ onDrop }}>
                      <UploadBody>
                        <UploadText>
                          <p className="text-body-3 font-medium text-metal-600">
                            Drag & Drop or Choose File to Upload
                          </p>
                          <p className="text-body-4 font-normal text-metal-400">
                            DOCX, XLSX, PPTX, PDF, and JPG formats, up to 50 MB.
                          </p>
                        </UploadText>
                      </UploadBody>
                      <UploadFooter isFileExists={files.length > 0}>
                        <p className="my-2 flex items-center gap-1 text-body-4 font-normal text-metal-600">
                          Uploaded Files
                        </p>
                        <ul className="space-y-1">
                          {files?.map(file => (
                            <li
                              key={file?.name}
                              className="flex items-center justify-between border-l-4 border-l-metal-100 bg-metal-25 px-4 py-2.5 text-left text-body-4 capitalize text-metal-600"
                            >
                              {file?.name}
                              <Button
                                color="primary"
                                shape="icon"
                                variant="softBg"
                                onClick={() => {
                                  const filteredFiles = files.filter(
                                    f => f.name !== file.name,
                                  )
                                  setFiles(filteredFiles)
                                }}
                              >
                                <Trash size={16} color="red" />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </UploadFooter>
                    </Upload>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSending}
                      >
                        Submeter Sinistro
                      </Button>
                    </div>
                  </form>
                </Card>
              )}
            </div>
          </>
        )}
      </Wrapper>
    </div>
  )
}
